import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import SignaturePad from "@/components/ResponsiveSignaturePad";
import { PhotoReportMIP } from "@/components/PhotoReportMIP";
import { getCurrentLocation } from "@/lib/geolocation";
import {
    Check, X, Minus, ChevronRight, ChevronLeft, Search, CheckCircle2,
    ClipboardList, PenLine, Loader2, Mic, MicOff,
} from "lucide-react";

export default function AuditFlow() {
    const { id } = useParams();
    const nav = useNavigate();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const [audit, setAudit] = useState(null);
    const [template, setTemplate] = useState([]);
    const [techs, setTechs] = useState([]);

    // Checklist step
    const [checklist, setChecklist] = useState({});
    const [evalTechId, setEvalTechId] = useState("");
    const [evalTechName, setEvalTechName] = useState("");
    const [techSearch, setTechSearch] = useState("");
    const [techOpen, setTechOpen] = useState(false);
    const [auditDate, setAuditDate] = useState(new Date().toISOString().slice(0, 10));

    // Receipt step
    const [clientNote, setClientNote] = useState("");
    const [acceptedBy, setAcceptedBy] = useState("");
    const [done, setDone] = useState(false);
    const sigRef = useRef(null);

    // Done screen — photo report presence + client email request
    const [hasPhotoReport, setHasPhotoReport] = useState(false);
    const [reportEmail, setReportEmail] = useState("");
    const [emailInput, setEmailInput] = useState("");
    const [savingEmail, setSavingEmail] = useState(false);
    const [reportWhatsapp, setReportWhatsapp] = useState("");
    const [waInput, setWaInput] = useState("");
    const [savingWa, setSavingWa] = useState(false);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [aRes, tRes, techRes] = await Promise.all([
                    api.get(`/audits/service/${id}`),
                    api.get("/audits/checklist-template"),
                    api.get("/audits/technicians"),
                ]);
                setAudit(aRes.data);
                setTemplate(tRes.data || []);
                setTechs(techRes.data || []);
                setChecklist(aRes.data?.checklist || {});
                setEvalTechId(aRes.data?.evaluated_technician_id || "");
                setEvalTechName(aRes.data?.evaluated_technician_name || "");
                if (aRes.data?.audit_date) setAuditDate(aRes.data.audit_date);
                setClientNote(aRes.data?.client_note || "");
                setAcceptedBy(aRes.data?.accepted_by_name || "");
                if (aRes.data?.status === "completed") setDone(true);
            } catch (e) {
                toast.error(e?.response?.data?.detail || "No se pudo cargar la auditoría");
            } finally { setLoading(false); }
        })();
    }, [id]);

    const setItem = (key, status) => {
        setChecklist((c) => {
            const cur = c[key]?.status;
            return { ...c, [key]: { ...(c[key] || {}), status: cur === status ? null : status } };
        });
    };
    const setComment = (key, comment) => setChecklist((c) => ({ ...c, [key]: { ...(c[key] || {}), comment } }));

    const saveChecklist = async () => {
        setBusy(true);
        try {
            await api.put(`/audits/service/${id}/checklist`, {
                evaluated_technician_id: evalTechId || null,
                evaluated_technician_name: evalTechName || null,
                audit_date: auditDate,
                checklist,
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo guardar el checklist");
            throw e;
        } finally { setBusy(false); }
    };

    const goToStep3 = async () => { try { await saveChecklist(); setStep(3); } catch { /* stay */ } };

    // When the audit is finished, check if a photo report exists to choose the message + load any saved email
    useEffect(() => {
        if (!done) return;
        (async () => {
            try {
                const { data } = await api.get(`/photo-reports/${id}`);
                setHasPhotoReport(!!(data?.photos && data.photos.length));
            } catch { setHasPhotoReport(false); }
        })();
        setReportEmail(audit?.client_report_email || "");
        setReportWhatsapp(audit?.client_report_whatsapp || "");
    }, [done, id, audit]);

    const addReportEmail = async () => {
        const e = emailInput.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { toast.error("Ingrese un correo válido"); return; }
        setSavingEmail(true);
        try {
            await api.post(`/audits/service/${id}/report-email`, { email: e });
            setReportEmail(e);
            setEmailInput("");
            toast.success("Correo agregado");
        } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo guardar el correo"); }
        finally { setSavingEmail(false); }
    };

    const removeReportEmail = async () => {
        setSavingEmail(true);
        try {
            await api.post(`/audits/service/${id}/report-email`, { email: null });
            setReportEmail("");
            toast.success("Correo eliminado");
        } catch { toast.error("No se pudo eliminar el correo"); }
        finally { setSavingEmail(false); }
    };

    const addReportWhatsapp = async () => {
        const digits = waInput.replace(/\D/g, "");
        if (digits.length !== 8) { toast.error("El número debe tener 8 dígitos"); return; }
        setSavingWa(true);
        try {
            const full = `+503${digits}`;
            await api.post(`/audits/service/${id}/report-whatsapp`, { whatsapp: full });
            setReportWhatsapp(full);
            setWaInput("");
            toast.success("WhatsApp agregado");
        } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo guardar el WhatsApp"); }
        finally { setSavingWa(false); }
    };

    const removeReportWhatsapp = async () => {
        setSavingWa(true);
        try {
            await api.post(`/audits/service/${id}/report-whatsapp`, { whatsapp: null });
            setReportWhatsapp("");
            toast.success("WhatsApp eliminado");
        } catch { toast.error("No se pudo eliminar el WhatsApp"); }
        finally { setSavingWa(false); }
    };

    const finalize = async () => {
        setBusy(true);
        try {
            let sig = null;
            if (sigRef.current && !sigRef.current.isEmpty()) sig = sigRef.current.toDataURL("image/png");
            const geo = await getCurrentLocation({ timeoutMs: 10000, enableHighAccuracy: true }).catch(() => ({ ok: false }));
            await api.post(`/audits/service/${id}/receipt`, {
                client_note: clientNote.trim() || null,
                accepted_by_name: acceptedBy.trim() || null,
                client_signature_base64: sig,
                latitude: geo.ok ? geo.latitude : null,
                longitude: geo.ok ? geo.longitude : null,
                accuracy_m: geo.ok ? geo.accuracy_m : null,
            });
            setDone(true);
            toast.success("Auditoría finalizada");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo finalizar");
        } finally { setBusy(false); }
    };

    const filteredTechs = useMemo(() => {
        const q = techSearch.trim().toLowerCase();
        return q ? techs.filter((t) => t.full_name.toLowerCase().includes(q)) : techs;
    }, [techs, techSearch]);

    if (loading) return <div className="p-4 space-y-3">{[1, 2, 3].map(i => <div key={i} className="h-24 rounded-xl bg-secondary animate-pulse" />)}</div>;

    if (done) {
        return (
            <div className="p-6 text-center" data-testid="audit-done">
                <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-3" />
                <h2 className="text-xl font-semibold mb-1">Auditoría finalizada</h2>
                {hasPhotoReport ? (
                    <>
                        <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto" data-testid="audit-done-msg-photo">
                            Encontrará el reporte fotográfico con los hallazgos y sus recomendaciones en 24 horas dentro de su cuenta de Truly IA360.
                        </p>
                        <Card className="p-4 max-w-md mx-auto text-left mb-6">
                            {reportEmail ? (
                                <div data-testid="audit-email-saved">
                                    <div className="flex items-center gap-1.5 text-emerald-700 text-sm font-medium">
                                        <CheckCircle2 className="w-4 h-4" /> Correo agregado
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Se le enviará el reporte por correo una vez aprobado.
                                    </p>
                                    <div className="flex items-center gap-2 mt-2">
                                        <span className="flex-1 text-sm font-medium bg-secondary rounded-md px-3 py-1.5 truncate" data-testid="audit-email-chip">{reportEmail}</span>
                                        <Button variant="ghost" size="sm" className="text-red-600" onClick={removeReportEmail} disabled={savingEmail} data-testid="audit-email-remove">
                                            {savingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : "Quitar"}
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <Label className="text-xs font-semibold block mb-1">Si desea que además se le envíe este reporte por correo, agréguelo aquí</Label>
                                    <div className="flex gap-2">
                                        <Input type="email" inputMode="email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)}
                                            placeholder="correo@empresa.com" data-testid="audit-email-input" />
                                        <Button onClick={addReportEmail} disabled={savingEmail} className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0" data-testid="audit-email-add">
                                            {savingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : "Agregar"}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </Card>

                        <Card className="p-4 max-w-md mx-auto text-left mb-6">
                            {reportWhatsapp ? (
                                <div data-testid="audit-wa-saved">
                                    <div className="flex items-center gap-1.5 text-emerald-700 text-sm font-medium">
                                        <CheckCircle2 className="w-4 h-4" /> WhatsApp agregado
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Se le enviará el reporte por WhatsApp una vez aprobado.
                                    </p>
                                    <div className="flex items-center gap-2 mt-2">
                                        <span className="flex-1 text-sm font-medium bg-secondary rounded-md px-3 py-1.5 truncate" data-testid="audit-wa-chip">🇸🇻 {reportWhatsapp}</span>
                                        <Button variant="ghost" size="sm" className="text-red-600" onClick={removeReportWhatsapp} disabled={savingWa} data-testid="audit-wa-remove">
                                            {savingWa ? <Loader2 className="w-4 h-4 animate-spin" /> : "Quitar"}
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <Label className="text-xs font-semibold block mb-1">O si prefiere recibirlo por WhatsApp, agregue su número aquí</Label>
                                    <div className="flex gap-2">
                                        <div className="flex items-center gap-1 px-3 rounded-md border bg-secondary text-sm shrink-0 whitespace-nowrap">🇸🇻 +503</div>
                                        <Input type="tel" inputMode="numeric" maxLength={8} value={waInput}
                                            onChange={(e) => setWaInput(e.target.value.replace(/\D/g, "").slice(0, 8))}
                                            placeholder="7000 0000" data-testid="audit-wa-input" />
                                        <Button onClick={addReportWhatsapp} disabled={savingWa} className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0" data-testid="audit-wa-add">
                                            {savingWa ? <Loader2 className="w-4 h-4 animate-spin" /> : "Agregar"}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </Card>
                    </>
                ) : (
                    <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto" data-testid="audit-done-msg-checklist">
                        Estamos comprometidos con su satisfacción y con la mejora continua.
                    </p>
                )}
                <Button onClick={() => nav("/audit")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="audit-finish-home">Volver a mi ruta</Button>
            </div>
        );
    }

    return (
        <div className="p-4 pb-28" data-testid="audit-flow-page">
            {/* Progress */}
            <div className="flex items-center gap-2 mb-4">
                {[1, 2, 3].map((n) => (
                    <div key={n} className={`flex-1 h-1.5 rounded-full ${step >= n ? "bg-emerald-500" : "bg-secondary"}`} />
                ))}
            </div>
            <div className="text-xs text-muted-foreground mb-3">Paso {step} de 3 · {step === 1 ? "Reporte fotográfico" : step === 2 ? "Checklist de supervisión" : "Firma de recibido"}</div>

            {/* STEP 1: PHOTO REPORT (mismo componente MIP del técnico) */}
            {step === 1 && (
                <div data-testid="audit-step-photo">
                    <PhotoReportMIP serviceId={id} />
                </div>
            )}

            {/* STEP 2: CHECKLIST */}
            {step === 2 && (
                <div data-testid="audit-step-checklist">
                    <div className="flex items-center gap-2 mb-3">
                        <ClipboardList className="w-5 h-5 text-[hsl(var(--trulyn-navy))]" />
                        <h2 className="font-semibold">Checklist de supervisión <span className="text-xs text-muted-foreground font-normal">(opcional)</span></h2>
                    </div>

                    {/* Header fields */}
                    <Card className="p-3 mb-3 space-y-3">
                        <div>
                            <Label className="text-xs font-semibold">Cliente</Label>
                            <Input value={audit?.client_name || ""} disabled className="mt-1 bg-secondary" data-testid="audit-cl-client" />
                        </div>
                        <div className="relative">
                            <Label className="text-xs font-semibold">Técnico evaluado</Label>
                            <div className="relative mt-1">
                                <Search className="w-4 h-4 absolute left-2 top-2.5 text-muted-foreground" />
                                <Input
                                    value={techOpen ? techSearch : evalTechName}
                                    onFocus={() => { setTechOpen(true); setTechSearch(""); }}
                                    onChange={(e) => setTechSearch(e.target.value)}
                                    placeholder="Buscar técnico..."
                                    className="pl-8"
                                    data-testid="audit-cl-tech-search"
                                />
                            </div>
                            {techOpen && (
                                <div className="absolute z-30 left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-56 overflow-y-auto" data-testid="audit-cl-tech-list">
                                    {filteredTechs.length === 0 ? (
                                        <div className="p-3 text-sm text-muted-foreground">Sin resultados</div>
                                    ) : filteredTechs.map((t) => (
                                        <button key={t.id}
                                            className="w-full text-left px-3 py-2 text-sm hover:bg-secondary"
                                            onClick={() => { setEvalTechId(t.id); setEvalTechName(t.full_name); setTechOpen(false); }}
                                            data-testid={`audit-cl-tech-${t.id}`}>
                                            {t.full_name} {t.code ? <span className="text-muted-foreground text-xs">({t.code})</span> : null}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-semibold">Fecha</Label>
                                <Input type="date" value={auditDate} onChange={(e) => setAuditDate(e.target.value)} className="mt-1" data-testid="audit-cl-date" />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">Auditor</Label>
                                <Input value={audit?.auditor_name || ""} disabled className="mt-1 bg-secondary" data-testid="audit-cl-auditor" />
                            </div>
                        </div>
                    </Card>

                    {/* Sections */}
                    {template.map((sec) => (
                        <Card key={sec.section} className="p-3 mb-3">
                            <div className="text-sm font-semibold mb-2">{sec.title}</div>
                            {sec.type === "items" && (
                                <div className="space-y-2">
                                    {sec.items.map((it) => (
                                        <ChecklistItem key={it.key} itemKey={it.key} label={it.label}
                                            value={checklist[it.key]} onSet={setItem} onComment={setComment} />
                                    ))}
                                </div>
                            )}
                            {sec.type === "steps_zones" && (
                                <div className="space-y-3">
                                    {sec.steps.map((stp) => (
                                        <div key={stp.key} className="border rounded-lg p-2">
                                            <div className="text-sm font-medium mb-1.5">{stp.label}</div>
                                            <div className="grid grid-cols-3 gap-2">
                                                {sec.zones.map((z) => {
                                                    const key = `${sec.section}_${stp.key}_${z.key}`;
                                                    const val = checklist[key]?.status;
                                                    return (
                                                        <div key={z.key} className="flex flex-col items-center gap-1">
                                                            <span className="text-[11px] text-muted-foreground">{z.label}</span>
                                                            <div className="flex gap-1">
                                                                <MiniBtn active={val === "ok"} variant="ok" onClick={() => setItem(key, "ok")} testid={`cl-${key}-ok`} />
                                                                <MiniBtn active={val === "fail"} variant="fail" onClick={() => setItem(key, "fail")} testid={`cl-${key}-fail`} />
                                                                <MiniBtn active={val === "na"} variant="na" onClick={() => setItem(key, "na")} testid={`cl-${key}-na`} />
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            {sec.zones.some((z) => checklist[`${sec.section}_${stp.key}_${z.key}`]?.status === "fail") && (
                                                <div className="mt-2 space-y-2">
                                                    {sec.zones.map((z) => {
                                                        const key = `${sec.section}_${stp.key}_${z.key}`;
                                                        if (checklist[key]?.status !== "fail") return null;
                                                        return (
                                                            <CommentField
                                                                key={z.key}
                                                                label={`${z.label} — ¿por qué está mal?`}
                                                                value={checklist[key]?.comment || ""}
                                                                onChange={(v) => setComment(key, v)}
                                                                placeholder="Describa el hallazgo o motivo del reprobado..."
                                                                testid={`cl-${key}-comment`}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                    ))}
                </div>
            )}

            {/* STEP 3: RECEIPT */}
            {step === 3 && (
                <div data-testid="audit-step-receipt">
                    <div className="flex items-center gap-2 mb-3">
                        <PenLine className="w-5 h-5 text-[hsl(var(--trulyn-navy))]" />
                        <h2 className="font-semibold">Firma de recibido</h2>
                    </div>
                    <Card className="p-3 mb-3">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Resumen de tiempos</div>
                        <div className="text-sm flex justify-between"><span>Inicio</span><span className="font-medium">{fmtTime(audit?.started_at)}</span></div>
                        <div className="text-sm flex justify-between"><span>Fin</span><span className="font-medium">{fmtTime(new Date().toISOString())}</span></div>
                    </Card>
                    <Card className="p-3 mb-3 space-y-3">
                        <div>
                            <Label className="text-xs font-semibold">Nota del cliente (opcional)</Label>
                            <Textarea value={clientNote} onChange={(e) => setClientNote(e.target.value)} placeholder="Observaciones del cliente..." className="mt-1" data-testid="audit-receipt-note" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">Nombre de quien recibe</Label>
                            <Input value={acceptedBy} onChange={(e) => setAcceptedBy(e.target.value)} placeholder="Nombre y cargo" className="mt-1" data-testid="audit-receipt-by" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">Firma de recibido</Label>
                            <div className="border rounded-lg overflow-hidden mt-1">
                                <SignaturePad ref={sigRef} height={160} />
                            </div>
                            <button className="text-xs text-muted-foreground mt-1" onClick={() => sigRef.current?.clear()} data-testid="audit-sig-clear">Limpiar firma</button>
                        </div>
                        <p className="text-[11px] text-muted-foreground">Al finalizar se registra la hora, la IP y la ubicación como constancia de recepción.</p>
                    </Card>
                </div>
            )}

            {/* Footer nav */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-3 z-20">
                <div className="max-w-2xl mx-auto flex gap-2">
                    {step === 2 && (
                        <Button variant="outline" onClick={() => setStep(1)} disabled={busy} data-testid="audit-back-photos">
                            <ChevronLeft className="w-4 h-4 mr-1" /> Fotos
                        </Button>
                    )}
                    {step === 3 && (
                        <Button variant="outline" onClick={() => setStep(2)} disabled={busy} data-testid="audit-prev-btn">
                            <ChevronLeft className="w-4 h-4 mr-1" /> Atrás
                        </Button>
                    )}
                    {step === 1 && (
                        <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setStep(2)} data-testid="audit-next-1">
                            Siguiente <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                    )}
                    {step === 2 && (
                        <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={goToStep3} disabled={busy} data-testid="audit-next-2">
                            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Siguiente <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                    )}
                    {step === 3 && (
                        <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={finalize} disabled={busy} data-testid="audit-finalize-btn">
                            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />} Finalizar auditoría
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}

function ChecklistItem({ itemKey, label, value, onSet, onComment }) {
    const [showComment, setShowComment] = useState(!!value?.comment);
    return (
        <div className="border rounded-lg p-2" data-testid={`cl-item-${itemKey}`}>
            <div className="flex items-start gap-2">
                <div className="flex-1 text-sm">{label}</div>
                <div className="flex gap-1 shrink-0">
                    <MiniBtn active={value?.status === "ok"} variant="ok" onClick={() => onSet(itemKey, "ok")} testid={`cl-${itemKey}-ok`} />
                    <MiniBtn active={value?.status === "fail"} variant="fail" onClick={() => onSet(itemKey, "fail")} testid={`cl-${itemKey}-fail`} />
                    <MiniBtn active={value?.status === "na"} variant="na" onClick={() => onSet(itemKey, "na")} testid={`cl-${itemKey}-na`} />
                </div>
            </div>
            {value?.status === "fail" && (
                <div className="mt-2">
                    <CommentField
                        label="¿Por qué está mal?"
                        value={value?.comment || ""}
                        onChange={(v) => onComment(itemKey, v)}
                        placeholder="Describa el hallazgo o motivo del reprobado..."
                        testid={`cl-${itemKey}-comment`}
                    />
                </div>
            )}
        </div>
    );
}

function CommentField({ value, onChange, label, placeholder, testid }) {
    const [listening, setListening] = useState(false);
    const recRef = useRef(null);
    const toggleVoice = () => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) { toast.error("Dictado por voz no disponible en este dispositivo"); return; }
        if (listening) { recRef.current?.stop(); setListening(false); return; }
        const rec = new SR();
        rec.lang = "es-SV"; rec.continuous = false; rec.interimResults = false;
        rec.onresult = (ev) => {
            const t = ev.results[0][0].transcript;
            const cur = (value || "").trim();
            onChange(cur ? `${cur} ${t}` : t);
        };
        rec.onerror = () => setListening(false);
        rec.onend = () => setListening(false);
        rec.start(); recRef.current = rec; setListening(true);
    };
    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <Label className="text-[11px] font-medium text-red-600">{label}</Label>
                <Button variant="ghost" size="icon" className={`h-7 w-7 ${listening ? "text-red-500 animate-pulse" : "text-muted-foreground"}`}
                    onClick={toggleVoice} data-testid={`${testid}-mic`} title={listening ? "Detener dictado" : "Dictar por voz"}>
                    {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </Button>
            </div>
            <Textarea rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="text-xs resize-none" data-testid={testid} />
        </div>
    );
}

function MiniBtn({ active, variant = "ok", onClick, testid }) {
    const styles = {
        ok:   { on: "bg-emerald-500 border-emerald-500 text-white", off: "border-emerald-300 text-emerald-500", Icon: Check },
        fail: { on: "bg-red-500 border-red-500 text-white",         off: "border-red-300 text-red-500",         Icon: X },
        na:   { on: "bg-gray-400 border-gray-400 text-white",       off: "border-gray-300 text-gray-400",        Icon: Minus },
    };
    const s = styles[variant] || styles.ok;
    const Icon = s.Icon;
    return (
        <button onClick={onClick} data-testid={testid} title={variant === "na" ? "No aplica" : variant === "ok" ? "Aprobado" : "Reprobado"}
            className={`w-9 h-9 rounded-lg flex items-center justify-center border-2 transition-all ${active ? s.on : s.off}`}>
            <Icon className="w-4 h-4" />
        </button>
    );
}

function fmtTime(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleTimeString("es-SV", { hour: "2-digit", minute: "2-digit" }); }
    catch { return "—"; }
}
